import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Page-based pagination dots for resource-list carousels.
 *
 * Resolves the active dot from the scroller's scroll position, not the slideshow's
 * reported slide index: on peek/gutter multi-visible carousels that index can be a
 * previous-page slide at the clamped last page, desyncing the dot. Updates only on
 * scroll settle (scrollend, debounce fallback) to avoid mid-scroll flicker.
 *
 * @extends {Component}
 */
class ResourceListPagination extends Component {
  /** @type {HTMLElement | null} */
  #slideshow = null;

  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {ReturnType<typeof setTimeout> | 0} */
  #settleTimer = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#slideshow = this.closest('slideshow-component');
    this.#scroller = this.#slideshow?.querySelector('[ref="scroller"]') ?? null;

    if (this.#scroller) {
      if ('onscrollend' in this.#scroller) {
        this.#scroller.addEventListener('scrollend', this.#handleSettle, { passive: true });
      } else {
        this.#scroller.addEventListener('scroll', this.#handleScroll, { passive: true });
      }
    } else if (this.#slideshow) {
      // No scroller found — fall back to the slideshow's reported slide index.
      this.#slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#handleSelect);
    }

    this.addEventListener('click', this.#handleClick);

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#slideshow?.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSelect);
    this.#scroller?.removeEventListener('scrollend', this.#handleSettle);
    this.#scroller?.removeEventListener('scroll', this.#handleScroll);
    this.removeEventListener('click', this.#handleClick);
    if (this.#settleTimer) clearTimeout(this.#settleTimer);
  }

  #handleClick = (/** @type {Event} */ event) => {
    const target = /** @type {Element | null} */ (event.target);
    const dot = target?.closest?.('[data-page-index]');
    if (!dot) return;
    const set = dot.closest('[data-page-size]');
    if (!set) return;
    const pageIndex = parseInt(dot.getAttribute('data-page-index') ?? '', 10);
    if (!Number.isFinite(pageIndex)) return;
    // Immediate feedback; the scroll settle reconfirms via #sync.
    this.#setActive(set, pageIndex);
  };

  #handleSettle = () => {
    this.#sync();
  };

  // Fallback for browsers without `scrollend`: debounce until scrolling stops.
  #handleScroll = () => {
    if (this.#settleTimer) clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(this.#handleSettle, 120);
  };

  #handleSelect = (/** @type {CustomEvent} */ event) => {
    const index = Number(event.detail?.index ?? 0);
    this.#syncFromIndex(Number.isFinite(index) ? index : 0);
  };

  #sync() {
    const scroller = this.#scroller;
    const sets = this.querySelectorAll('[data-page-size]');

    for (const set of sets) {
      const pageCount = set.querySelectorAll('[data-page-index]').length;
      if (!pageCount) continue;

      let activePage = 0;
      if (scroller && pageCount > 1) {
        const maxX = scroller.scrollWidth - scroller.clientWidth;
        const maxY = scroller.scrollHeight - scroller.clientHeight;
        const horizontal = maxX >= maxY;
        const max = horizontal ? maxX : maxY;
        const position = Math.abs(horizontal ? scroller.scrollLeft : scroller.scrollTop);
        const fraction = max > 0 ? Math.min(1, Math.max(0, position / max)) : 0;
        activePage = Math.round(fraction * (pageCount - 1));
      }
      this.#setActive(set, activePage);
    }
  }

  /**
   * Fallback used only when no scroller is found. Picks the last dot whose target
   * slide index is <= the current index, to match the dots' clamped nav targets.
   * @param {number} slideIndex
   */
  #syncFromIndex(slideIndex) {
    const sets = this.querySelectorAll('[data-page-size]');
    for (const set of sets) {
      const dots = Array.from(set.querySelectorAll('[data-page-index]'));
      let activePage = -1;
      for (let i = 0; i < dots.length; i++) {
        const start = parseInt(dots[i].getAttribute('data-slide-index') ?? '', 10);
        if (Number.isFinite(start) && slideIndex >= start) activePage = i;
      }
      if (activePage === -1) {
        const pageSize = Math.max(1, parseInt(set.getAttribute('data-page-size') ?? '1', 10));
        activePage = Math.max(0, Math.floor(slideIndex / pageSize));
      }
      this.#setActive(set, activePage);
    }
  }

  /**
   * @param {Element} set
   * @param {number} activePage
   */
  #setActive(set, activePage) {
    const dots = set.querySelectorAll('[data-page-index]');
    dots.forEach((dot, i) => {
      dot.setAttribute('aria-selected', String(i === activePage));
    });
  }
}

if (!customElements.get('resource-list-pagination')) {
  customElements.define('resource-list-pagination', ResourceListPagination);
}
